import { readStayAccess, reissueStayAccess } from '@/api/core/access-members';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { Button, DetailPage } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { AccessRuntime } from './stay-access-section';

/** 투숙 출입 멤버와 장비 상태를 조회하고 재발급을 명시적으로 확인한다. */
export function StayUnifiedAccess({ runtime, stayId }: { runtime: AccessRuntime; stayId: string }) {
  const session = useOperatorSession(runtime.session);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const allowed =
    session.status === 'authorized' &&
    !!session.authorization &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(session.authorization, 'entitlement', 1);
  const writable =
    allowed && !!session.authorization && canReadDomain(session.authorization, 'entitlement', 4);
  const access = useQuery({
    queryKey: coreQueryKey(runtime.apiNamespace, session.generation, 'stay-access', stayId),
    queryFn: ({ signal }) => readStayAccess(runtime.client, stayId, signal),
    enabled: allowed,
  });
  const reissue = async () => {
    const actor = runtime.session.getSnapshot();
    if (
      !writable ||
      !confirm ||
      busy ||
      actor.generation !== session.generation ||
      actor.status !== 'authorized' ||
      !actor.authorization ||
      actor.deniedDomains.includes('entitlement') ||
      !canReadDomain(actor.authorization, 'entitlement', 4)
    )
      return;
    const generation = session.generation;
    setBusy(true);
    try {
      await reissueStayAccess(runtime.client, stayId);
      if (runtime.session.getSnapshot().generation === generation) {
        setMessage('재발급 요청이 접수되었습니다.');
        setConfirm(false);
      }
    } catch (error) {
      if (runtime.session.getSnapshot().generation === generation)
        setMessage(
          isApiError(error) && error.status === 409
            ? '현재 재발급할 수 없습니다. 상태를 확인해 주세요.'
            : '재발급을 요청하지 못했습니다.',
        );
    } finally {
      setBusy(false);
    }
  };
  if (!allowed) return null;
  return (
    <DetailPage.Section title="통합 출입">
      {access.isPending ? <p role="status">출입 정보를 조회하고 있습니다.</p> : null}
      {access.isError ? <p role="alert">출입 정보를 불러오지 못했습니다.</p> : null}
      {access.data ? (
        <>
          <p>도어락 상태: {access.data.doorlock.status}</p>
          {access.data.group_missing ? <p>층 출입 그룹이 없습니다.</p> : null}
          <ul>
            {access.data.items.map((item) => (
              <li key={item.id}>
                {item.source_kind} · {item.user_id ?? '비회원'} · {item.valid_from} ~{' '}
                {item.valid_until}
                {item.revoked_at ? ' · 회수됨' : ''}
              </li>
            ))}
          </ul>
          {access.data.unresolved.map((item) => (
            <p key={item}>{item}</p>
          ))}
          {writable && access.data.doorlock.device_id ? (
            <>
              <Button
                disabled={busy}
                onClick={() => {
                  setConfirm(true);
                }}
              >
                PIN 재발급
              </Button>
              {confirm ? (
                <Button disabled={busy} onClick={() => void reissue()}>
                  재발급 확인
                </Button>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
      {message ? <p role="status">{message}</p> : null}
    </DetailPage.Section>
  );
}
